import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ViewStyle, TextStyle } from 'react-native';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ADMIN_COLORS } from '@/constants/admin';

type AdminErrorStateProps = {
  title: string;
  message?: string;
  retryCount?: number;
  canRetry?: boolean;
  onRetry?: () => void;
  containerStyle?: ViewStyle;
  titleStyle?: TextStyle;
  messageStyle?: TextStyle;
  retryButtonText?: string;
};

/**
 * 
 * Use AdminErrorState inside normal UI branches (loading/error/success) driven by your hooks and data.
 * 
 */

export const AdminErrorState: React.FC<AdminErrorStateProps> = ({
  title,
  message,
  retryCount,
  canRetry,
  onRetry,
  containerStyle,
  titleStyle,
  messageStyle,
  retryButtonText = 'Try Again',
}) => {
  return (
    <View style={[styles.errorContainer, containerStyle]}>
      <SVG_COMPONENTS.X_CIRCLE width={48} height={48} color={ADMIN_COLORS.ERROR} />
      <Text style={[styles.errorTitle, titleStyle]}>{title}</Text>
      {!!message && (
        <Text style={[styles.errorMessage, messageStyle]}>
          {message}
        </Text>
      )}
      {typeof retryCount === 'number' && retryCount > 0 && (
        <Text style={styles.retryInfo}>Retry attempts: {retryCount}</Text>
      )}
      {canRetry && !!onRetry && (
        <TouchableOpacity style={styles.retryButton} onPress={onRetry}>
          <SVG_COMPONENTS.CHEVRON_RIGHT width={16} height={16} color="#FFFFFF" />
          <Text style={styles.retryButtonText}>{retryButtonText}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  errorMessage: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 20,
    maxWidth: 300,
  },
  retryInfo: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
    marginBottom: 16,
    fontStyle: 'italic',
  },
  retryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ADMIN_COLORS.PRIMARY,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default AdminErrorState;


